import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProFormDigit, ProFormColumnsType, BetaSchemaForm } from '@ant-design/pro-components';
import { Button, Form, Space, Tag } from 'antd';
import { IconBuilding, IconFileText } from '@tabler/icons-react';
import { CrudMode, renderListToTag, renderMultiListToTags, renderSwitchToTag } from '@ohpen/enso-ui';
import { ExportOutlined } from '@ant-design/icons';

import { FieldType, FirmGetById, FirmType, ActivityType } from '@pmp/api';
import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';
import { getHighlightProps } from '../utils';

export const RegistrationDetails: React.FC<TabProps> = ({
  form,
  mode,
  selectsOptions,
  populateFcaData,
  fcaModifiedFields,
  removeFcaModifiedField,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const readonly = mode === CrudMode.VIEW;
  const watchedDisabledActivities = Form.useWatch(['registrationDetails', 'disabledActivitiesPermissions'], form);
  const frn = Form.useWatch(['registrationDetails', 'frn'], form);
  const type = Form.useWatch(['registrationDetails', 'type'], form);

  const disabledActivitiesPermissions = useMemo(() => watchedDisabledActivities ?? [], [watchedDisabledActivities]);

  const isFcaRegistered = useMemo(() => type !== 'NOT_FCA_REGISTERED', [type]);

  const populateData = useCallback(() => {
    setLoading(true);
    if (populateFcaData) {
      populateFcaData(frn).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [frn, populateFcaData]);

  const activitiesOptions = useMemo(() => {
    const allOptions = [
      {
        label: t('lender.firms.labels.createCases'),
        value: ActivityType.CREATE_CASES,
        disabled: disabledActivitiesPermissions.includes(ActivityType.CREATE_CASES),
      },
      {
        label: t('lender.firms.labels.rmc'),
        value: ActivityType.REGULATED_MORTGAGE_CONTRACT_CASES,
        disabled: disabledActivitiesPermissions.includes(ActivityType.REGULATED_MORTGAGE_CONTRACT_CASES),
      },
      {
        label: t('lender.firms.labels.cbtl'),
        value: ActivityType.CONSUMER_BUY_TO_LET_CASES,
        disabled: disabledActivitiesPermissions.includes(ActivityType.CONSUMER_BUY_TO_LET_CASES),
      },
      {
        label: t('lender.firms.labels.btl'),
        value: ActivityType.BUY_TO_LET_CASES,
        disabled: disabledActivitiesPermissions.includes(ActivityType.BUY_TO_LET_CASES),
      },
    ];

    if (!isFcaRegistered) {
      return allOptions.filter(({ value }) => [ActivityType.CREATE_CASES, ActivityType.BUY_TO_LET_CASES].includes(value));
    }

    return allOptions;
  }, [isFcaRegistered, disabledActivitiesPermissions, t]);

  const firmColumns: ProFormColumnsType<FirmGetById, 'text'>[] = useMemo(
    () => [
      {
        title: t('lender.firms.labels.currentStatus'),
        dataIndex: ['registrationDetails', 'isActive'],
        valueType: 'switch',
        initialValue: mode === CrudMode.CREATE ? false : undefined,
        readonly,
        fieldProps: readonly
          ? undefined
          : {
            checkedChildren: t('lender.firms.labels.active'),
            unCheckedChildren: t('lender.firms.labels.inactive'),
          },
        render: renderSwitchToTag,
        colProps: { span: 24 },
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.type'),
            dataIndex: ['registrationDetails', 'type'],
            valueType: 'select',
            fieldProps: {
              options: selectsOptions[FieldType.FIRM_TYPE],
              tagcolor: 'blue',
              className: getHighlightProps(['registrationDetails', 'type'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'type'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.type')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
              rules: [{ required: true }],
            },
            readonly,
            render: renderListToTag,
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.principalFirm'),
            dataIndex: ['registrationDetails', 'principalIds'],
            valueType: 'select',
            fieldProps: {
              options: selectsOptions[FieldType.PRINCIPALS],
              mode: 'multiple',
              tagcolor: 'blue',
            },
            formItemProps: {
              rules: [{ required: true }],
            },
            readonly,
            render: renderMultiListToTags,
            hideInForm: type !== FirmType.APPOINTED_REPRESENTATIVE,
            colProps: { span: 12 },
          },
        ],
      },
      // {
      //   title: t('lender.firms.labels.currentStatus'),
      //   dataIndex: ['registrationDetails', 'currentStatus'],
      //   valueType: 'checkbox',
      //   fieldProps: {
      //     options: [{ label: t('lender.firms.labels.firmActive'), value: 'firmActive' }],
      //   },
      //   colProps: { span: 24 },
      // },
      {
        valueType: 'group',
        colProps: { span: 24 },
        columns: [
          {
            title: t('lender.firms.labels.frn'),
            dataIndex: ['registrationDetails', 'frn'],
            valueType: 'text',
            readonly,
            renderText: (val) => String(val),
            renderFormItem: () => (
              <Space.Compact style={{ width: '100%' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ProFormDigit
                    name={['registrationDetails', 'frn']}
                    min={1}
                    fieldProps={{
                      style: { width: '100%' },
                      controls: false,
                    }}
                    disabled={mode !== CrudMode.CREATE}
                  />
                </div>

                <Button type="primary" onClick={populateData} disabled={readonly} loading={loading}>
                  {t('lender.firms.buttons.populateData')}
                </Button>
              </Space.Compact>
            ),
            formItemProps: {
              tooltip: t('lender.firms.labels.frnPopulateTooltip'),
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
            hideInForm: type === FirmType.NOT_FCA_REGISTERED,
          },
          {
            title: t('lender.firms.labels.fcaRegister'),
            valueType: 'text',
            colProps: { span: 12 },
            renderFormItem: () => {
              const frn = form?.getFieldValue(['registrationDetails', 'frn']);
              if (!frn) return '-';

              const url = `https://register.fca.org.uk/s/search?q=${encodeURIComponent(frn)}&type=Companies`;

              return (
                <a href={url} target="_blank" rel="noreferrer">
                  {t('lender.firms.labels.goToWebsite')}
                  <ExportOutlined />
                </a>
              );
            },
            hideInForm: type === FirmType.NOT_FCA_REGISTERED,
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.name'),
            dataIndex: ['registrationDetails', 'name'],
            valueType: 'text',
            readonly,
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.name')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
              rules: [{ required: true }],
            },
            fieldProps: {
              className: getHighlightProps(['registrationDetails', 'name'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'name'),
            },

            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.reference'),
            dataIndex: ['registrationDetails', 'reference'],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.ohpenId'),
            dataIndex: ['registrationDetails', 'ohpenId'],
            valueType: 'text',
            readonly,
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [t, populateData, readonly, form, loading, selectsOptions, fcaModifiedFields, removeFcaModifiedField, type, isFcaRegistered, mode],
  );

  const registrationColumns: ProFormColumnsType<FirmGetById, 'text'>[] = useMemo(
    () => [
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.fcaRegisterStatus'),
            dataIndex: ['registrationDetails', 'fcaStatus'],
            valueType: 'text',
            readonly,
            fieldProps: {
              className: getHighlightProps(['registrationDetails', 'fcaStatus'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'fcaStatus'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.fcaRegisterStatus')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.nacfbRegistration'),
            dataIndex: ['registrationDetails', 'nacfbNumber'],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: 'group',
        columns: [
          {
            title: t('lender.firms.labels.operations'),
            dataIndex: ['registrationDetails', 'operations'],
            valueType: 'checkbox',
            fieldProps: {
              options: [
                { label: t('lender.firms.labels.givesAdvice'), value: 'ADVICE' },
                { label: t('lender.firms.labels.packaging'), value: 'PACKAGE' },
                { label: t('lender.firms.labels.actAsPrincipal'), value: 'NETWORK', disabled: type !== FirmType.DIRECTLY_AUTHORISED },
                { label: t('lender.firms.labels.mortgageClub'), value: 'MORTGAGE_CLUB' },
              ],
              disabled: readonly,
              style: {
                display: 'flex',
                flexDirection: 'column',
                rowGap: 4,
              },
            },
            formItemProps: {
              label: t('lender.firms.labels.operations'),
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.firms.labels.activitiesPermissions'),
            dataIndex: ['registrationDetails', 'activitiesPermissions'],
            valueType: 'checkbox',
            fieldProps: {
              options: activitiesOptions,
              disabled: readonly,
              style: {
                display: 'flex',
                flexDirection: 'column',
                rowGap: 4,
              },
              className: getHighlightProps(['registrationDetails', 'activitiesPermissions'], fcaModifiedFields),
              onBlur: () => removeFcaModifiedField?.('registrationDetails', 'activitiesPermissions'),
            },
            formItemProps: {
              label: (
                <span>
                  {t('lender.firms.labels.activitiesPermissions')}
                  {isFcaRegistered && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('lender.firms.labels.fca')}
                    </Tag>
                  )}
                </span>
              ),
            },
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [readonly, t, fcaModifiedFields, removeFcaModifiedField, type, activitiesOptions, isFcaRegistered],
  );

  return (
    <>
      <CrudFormCard icon={<IconBuilding />} title={t('lender.firms.labels.firmTitle')}>
        <BetaSchemaForm<FirmGetById> form={form} layoutType="Embed" columns={firmColumns} submitter={false} />
      </CrudFormCard>
      <CrudFormCard icon={<IconFileText />} title={t('lender.firms.labels.registrationDetails')}>
        <BetaSchemaForm<FirmGetById> form={form} layoutType="Embed" columns={registrationColumns} submitter={false} />
      </CrudFormCard>
    </>
  );
};
